'use client';

import React from 'react';
import {
  AlertTriangle, Globe, Database, TrendingUp,
  BarChart2, ShieldCheck, ArrowRight, MessageCircle, LineChart,
} from 'lucide-react';

const DATA_CATEGORIES = [
  {
    category: 'Fundamentals & Financials',
    color: 'sky',
    items: ['Company fundamentals & historical financials — updated within hours of every earnings release'],
  },
  {
    category: 'Real-Time Pricing',
    color: 'emerald',
    items: ['Enterprise-grade real-time pricing for U.S. stocks'],
  },
  {
    category: 'Analyst Intelligence',
    color: 'violet',
    items: ['Analyst ratings, price targets & individual analyst accuracy metrics'],
  },
  {
    category: 'Forecasts & Holdings',
    color: 'amber',
    items: ['Revenue & EPS forecasts, ETF holdings, and dividend history'],
  },
  {
    category: 'Corporate Actions',
    color: 'rose',
    items: ['Stock splits, mergers, ticker changes, and other corporate events'],
  },
  {
    category: 'Supplemental Data',
    color: 'cyan',
    items: ['Additional ETF holdings, dividend records & alternative download data'],
  },
];

const colorMap: Record<string, string> = {
  sky:    'bg-sky-500/10 border-sky-500/20 text-sky-400',
  emerald:'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  violet: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
  amber:  'bg-amber-500/10 border-amber-500/20 text-amber-400',
  rose:   'bg-rose-500/10 border-rose-500/20 text-rose-400',
  cyan:   'bg-cyan-500/10 border-cyan-500/20 text-cyan-400',
};

const dotMap: Record<string, string> = {
  sky:    'bg-sky-400',
  emerald:'bg-emerald-400',
  violet: 'bg-violet-400',
  amber:  'bg-amber-400',
  rose:   'bg-rose-400',
  cyan:   'bg-cyan-400',
};

function SectionIcon({ icon: Icon, color }: { icon: React.ElementType; color: string }) {
  return (
    <div className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={17} />
    </div>
  );
}

function openChat() {
  window.dispatchEvent(new CustomEvent('open-chat'));
}

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-surface">
      {/* Hero */}
      <div className="border-b border-default bg-surface">
        <div className="max-w-4xl mx-auto px-2 py-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-400" />
            </div>
            <span className="text-xs font-semibold tracking-widest text-fg-subtle uppercase">Data Disclaimer</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
            Data Quality &amp; Sources
          </h1>
          <p className="text-fg-muted text-base leading-relaxed max-w-2xl">
            All data is provided for informational purposes only and must not be used as the basis for
            trading or investment decisions. While we strive to maintain the highest standard of accuracy,
            no data service can guarantee perfection across thousands of symbols and sources.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-2 py-12 space-y-8">

        {/* Coverage */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={Globe} color="bg-sky-500/10 border-sky-500/20 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Coverage</h2>
          </div>
          <p className="text-fg-muted text-sm leading-relaxed">
            Our database spans global markets — stocks and ETFs across all major U.S.
            exchanges and international markets* alike. We&apos;re always working to close the gaps. If a symbol
            isn&apos;t showing up,{' '}
            <button onClick={openChat} className="text-sky-400 hover:text-sky-300 underline underline-offset-2 transition-colors">
              let us know
            </button>{' '}
            and we&apos;ll do our best to track it down.
          </p>
          <p className="text-xs text-slate-600 mt-3">* International markets coming soon</p>
        </div>

        {/* Data Sources */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <SectionIcon icon={Database} color="bg-violet-500/10 border-violet-500/20 text-violet-400" />
            <h2 className="text-lg font-semibold text-white">Data Sources</h2>
          </div>
          <p className="text-fg-muted text-sm leading-relaxed mb-5">
            We aggregate data from multiple institutional-grade providers, each selected for reliability
            and depth of coverage in their respective domain.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {DATA_CATEGORIES.map(({ category, color, items }) => (
              <div
                key={category}
                className="bg-surface border border-default rounded-xl p-4 flex flex-col gap-2"
              >
                <div className={`self-start text-xs font-semibold px-2.5 py-1 rounded-full border ${colorMap[color]}`}>
                  {category}
                </div>
                <ul className="space-y-1">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-fg-muted leading-relaxed">
                      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dotMap[color]}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={TrendingUp} color="bg-emerald-500/10 border-emerald-500/20 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Pricing</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              Real-time pricing for U.S. and Canadian stocks is delivered through{' '}
              <span className="text-white font-medium">licensed exchange feeds</span>. Consensus open and official
              close prices are sourced from major U.S. exchanges and reflect a{' '}
              <span className="text-white font-medium">15-minute delay</span>.
            </p>
            <p>
              For international stocks*, prices are shown with either a 15-minute delay or on an end-of-day basis —
              often closely approximating real time depending on the exchange. The exact timestamp of the most recent
              update is displayed directly beneath each price, shown in local exchange time.
            </p>
            <p className="text-xs text-slate-600">* International markets coming soon</p>
          </div>
        </div>

        {/* Analyst Estimates */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={BarChart2} color="bg-amber-500/10 border-amber-500/20 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Analyst Estimates &amp; Forecasts</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              Ratings and price targets are drawn from{' '}
              <span className="text-white font-medium">Wall Street professionals</span>, while revenue and EPS
              forecasts are aggregated from{' '}
              <span className="text-white font-medium">sell-side and buy-side analysts</span> via leading
              financial data providers.
            </p>
            <p>
              Keep in mind that different providers collect and aggregate analyst data in different ways — so numbers
              may differ slightly from other platforms, even if the overall direction is consistent.
            </p>
            <div className="flex items-start gap-3 mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
              <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300/80">
                All figures are estimates. Analysts are wrong often, and no forecast should be mistaken for a
                reliable outlook on where a stock is headed.
              </p>
            </div>
          </div>
        </div>

        {/* Quality & Accuracy */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={ShieldCheck} color="bg-rose-500/10 border-rose-500/20 text-rose-400" />
            <h2 className="text-lg font-semibold text-white">Quality &amp; Accuracy</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              Getting a stock or ETF onto the platform takes real work — it&apos;s not a fully automated process,
              and keeping data fresh is an ongoing effort. Markets move fast, and figures can go stale quickly
              despite our best efforts.
            </p>
            <p>
              We track financial events as closely as possible — new listings, ticker changes, and more — to keep
              our data as fresh as we can. That said, with so many symbols to maintain, things can slip through.
              Any issue you flag will be taken seriously and addressed as quickly as possible.
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={AlertTriangle} color="bg-red-500/10 border-red-500/20 text-red-400" />
            <h2 className="text-lg font-semibold text-white">Disclaimer</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              This site is intended for informational and entertainment purposes only. Nothing here constitutes
              financial advice, investment advice, or a recommendation to buy or sell any security. Always do your
              own research and consult a qualified financial professional before making any investment decisions.
            </p>
            <p>
              While we work hard to keep our data current, some information may be outdated, incomplete, or not
              applicable to your situation. Always verify figures against primary sources before acting on them.
            </p>
          </div>
        </div>

        {/* Trademarks & Logos */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={ShieldCheck} color="bg-indigo-500/10 border-indigo-500/20 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Trademarks &amp; Company Logos</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              Company logos, brand names, and trademarks displayed on this platform — including those shown in
              the market heatmap, stock detail pages, and other areas — are the property of their respective
              owners. This platform is not affiliated with, endorsed by, or sponsored by any of the companies
              whose logos or trademarks appear on this site.
            </p>
            <p>
              Logos are sourced from publicly available third-party services and are used solely for
              identification and informational purposes under nominative fair use. If you are a trademark
              owner and believe your logo is being used improperly, please contact us and we will promptly
              address your concern.
            </p>
          </div>
        </div>

        {/* Charting attribution — required by the Lightweight Charts NOTICE
            (Apache 2.0). Its README lets the on-chart logo satisfy the link
            requirement OR a link elsewhere on a user-facing page; the charts
            set attributionLogo:false, so this section is what satisfies it.
            Do not remove without restoring the on-chart mark. */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SectionIcon icon={LineChart} color="bg-sky-500/10 border-sky-500/20 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Charting Library</h2>
          </div>
          <div className="space-y-3 text-sm text-fg-muted leading-relaxed">
            <p>
              Interactive price charts on this platform are powered by{' '}
              <a
                href="https://www.tradingview.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 underline"
              >
                TradingView
              </a>
              {' '}Lightweight Charts™, used under the Apache License 2.0.
            </p>
            <p className="text-xs text-fg-subtle">
              Charting Library by TradingView, Inc. TradingView® is a registered trademark of
              TradingView, Inc. This platform is not affiliated with, endorsed by, or sponsored by
              TradingView.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card-bg border border-default rounded-2xl p-6">
          <div>
            <p className="text-sm font-medium text-white mb-0.5">Found an inaccuracy?</p>
            <p className="text-xs text-fg-subtle">We review every report and fix issues promptly.</p>
          </div>
          <button
            onClick={openChat}
            className="flex items-center gap-2 px-5 py-2.5 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <MessageCircle size={14} /> Contact Us
          </button>
        </div>

      </div>
    </div>
  );
}
